<template>
  <div class="points-page flex-1 relative" :class="{ 'pb-24': (activeNavigation.length > 1 || activeNavigation.includes('qr_code')) && showPointNavigation && currentDisplay !== 'qr_code' }">
    <!-- Task list (only show in Points overview when game module is active) -->
    <TaskList v-if="isGameModuleActive" />
    
    <div v-if="localLoading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mx-auto"></div>
        <p class="mt-4">{{ $t('loading', 'Loading...') }}</p>
      </div>
    </div>
    
    <!-- Display points based on currentDisplay -->
    <div v-else-if="Object.keys(visiblePoints || {}).length > 0 || activeNavigation.length === 0" class="w-full h-full relative">
      <!-- Outdoor Map view (MapLibre) -->
      <OutdoorMap 
        v-if="currentDisplay === 'outdoor_map' && (tourSettings?.navigation?.open_streetmap || tourSettings?.navigation?.google_maps)"
        :key="`outdoor-map-${Object.keys(visiblePoints || {}).length}-${Object.keys(visiblePoints || {}).join(',')}`"
        :currentLocation="currentLocation"
        :points="visiblePoints"
        :onItemClick="handleItemClick"
        :isPointGPSLocked="isPointGPSLocked"
        class="w-full relative"
      />
      
      <!-- Map view (static map image) -->
      <VueMap 
        v-else-if="currentDisplay === 'map' && tourSettings?.navigation?.map"
        :key="`vue-map-${Object.keys(visiblePoints || {}).length}-${Object.keys(visiblePoints || {}).join(',')}`"
        :currentLocation="currentLocation"
        :points="visiblePoints"
        :onItemClick="handleItemClick"
        class="w-full relative"
      />
      
      <!-- QR Code Scanner -->
      <QRCodeScanner 
        v-else-if="currentDisplay === 'qr_code'"
      />
      
      <!-- Default list view for other displays -->
      <VueList 
        v-else
        :currentLocation="currentLocation"
        :points="visiblePoints"
        :onItemClick="handleItemClick"
        :isPointGPSLocked="isPointGPSLocked"
        class="w-full relative"
      />  

    </div>
    
    <div v-else class="text-center py-8">
      <p></p>
    </div>

    <!-- Point Preview -->
    <PointPreview 
      v-if="isPreviewOpen && previewPoint"
      :isPreviewOpen="isPreviewOpen"
      :previewPoint="previewPoint"
      :onPreviewAction="handlePreviewAction"
      :onNavigationAction="handleNavigationAction"
      @close-preview="closePreview"
    />

    <!-- Module Status Panel -->
    <!-- <div class="fixed p-4" style="z-index: 60; width: 300px; right: 4em; top: 4em;">
      <ModuleStatus />
    </div> -->

    <!-- Navigation Bar -->
    <PointNavigationBar 
      :selectedView="selectedView"
      :currentDisplay="currentDisplay"
      :isPreviewOpen="isPreviewOpen"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { markRaw, h } from 'vue'
import TourPoint from './TourPoint.vue'
import PointNavigationBar from '../components/PointNavigationBar.vue'
import VueList from '../components/List.vue'
import VueMap from '../components/Map.vue'
import OutdoorMap from '../components/OutdoorMap.vue'
import PointPreview from '../components/PointPreview.vue'
import QRCodeScanner from '../components/QRCodeScanner.vue'
import ModuleStatus from '../components/ModuleStatus.vue'
import TaskList from '../components/TaskList.vue'
import { showMultiLingualText } from '../utils/languageUtils'
import moduleManager from '../modules/index.js'

export default {
  name: 'VuePoints',
  components: {
    TourPoint,
    PointNavigationBar,
    VueList,
    VueMap,
    OutdoorMap,
    PointPreview,
    QRCodeScanner,
    ModuleStatus,
    TaskList
  },
  props: {
    selectedView: {
      type: String,
      default: null
    },
    tourId: {
      type: String,
      default: null
    },
    pointId: {
      type: String,
      default: null
    }
  },
  setup() {
    // Use the Vuex store directly
    return {}
  },
  computed: {
    ...mapGetters('tours', ['activeTour']),
    ...mapGetters('settings', ['tourSettings', 'loading']),
    ...mapGetters('visited', ['visitedPoints']),
    ...mapGetters('pointNavigation', ['pointNavigation', 'showPointNavigation']),
    ...mapGetters('location', ['currentLocation', 'isValidLocation']),
    ...mapGetters('pointPreview', ['isPreviewOpen', 'previewPoint']),

    // watch for overlay visible from reactive state in useOverlay
    isOverlayVisible() {
      return this.$store.getters['overlay/isVisible']
    },
    
    // Get active navigation options from tour settings
    activeNavigation() {
      console.log('=== ACTIVE NAVIGATION COMPUTED ===')
      console.log('selectedView:', this.selectedView)
      console.log('tourSettings:', this.tourSettings)
      console.log('tourSettings.navigation:', this.tourSettings?.navigation)
      console.log('tourSettings.navigation keys:', Object.keys(this.tourSettings?.navigation ?? {}))
      
      if (this.selectedView) {
        console.log('selectedView is set, returning empty array')
        return []
      }
      
      const activeKeys = Object.keys(this.tourSettings?.navigation ?? {}).filter(key => {
        const isActive = this.tourSettings?.navigation?.[key]?.active
        console.log(`Navigation key "${key}": active = ${isActive}`)
        return isActive
      })
      
      console.log('Active navigation keys:', activeKeys)
      console.log('Filtered navigation settings:', activeKeys.map(key => ({
        key,
        settings: this.tourSettings?.navigation?.[key]
      })))
      
      return activeKeys
    },
    
    // Current display mode
    currentDisplay() {
      console.log('=== CURRENT DISPLAY COMPUTED ===')
      console.log('selectedView:', this.selectedView)
      console.log('pointNavigation:', this.pointNavigation)
      const result = this.selectedView || this.pointNavigation
      console.log('currentDisplay result:', result)
      return result
    },
    
    // Module state checks
    isGameModuleActive() {
      return moduleManager.isModuleActive('game')
    },
    
    isRoutingModuleActive() {
      return moduleManager.isModuleActive('routing')
    },
    
    // Get game state if available
    gameState() {
      if (this.isGameModuleActive) {
        return moduleManager.getModuleData('game')
      }
      return null
    },
    
    // Get routing state if available
    routingState() {
      if (this.isRoutingModuleActive) {
        return moduleManager.getModuleData('routing')
      }
      return null
    },
    
    // Check if this is an outdoor tour (outdoor_map navigation available)
    isOutdoorTour() {
      console.log('=== IS OUTDOOR TOUR COMPUTED ===')
      console.log('selectedView:', this.selectedView)
      console.log('pointNavigation:', this.pointNavigation)
      console.log('currentDisplay:', this.currentDisplay)
      console.log('activeNavigation:', this.activeNavigation)
      console.log('tourSettings.navigation:', this.tourSettings?.navigation)
      
      // If we have a selectedView, check if it's outdoor_map
      if (this.selectedView) {
        const result = this.selectedView === 'outdoor_map'
        console.log('Using selectedView, isOutdoorTour:', result)
        return result
      }
      
      // If we have pointNavigation set to outdoor_map, it's an outdoor tour
      if (this.pointNavigation === 'outdoor_map') {
        console.log('Using pointNavigation, isOutdoorTour: true')
        return true
      }
      
      // If currentDisplay is outdoor_map, it's an outdoor tour
      if (this.currentDisplay === 'outdoor_map') {
        console.log('Using currentDisplay, isOutdoorTour: true')
        return true
      }
      
      // Check if outdoor map navigation options are available (google_maps or open_streetmap)
      const hasOutdoorMapNavigation = this.activeNavigation.includes('google_maps') || 
                                     this.activeNavigation.includes('open_streetmap')
      console.log('Has outdoor map navigation (google_maps/open_streetmap):', hasOutdoorMapNavigation)
      
      if (hasOutdoorMapNavigation) {
        console.log('Using outdoor map navigation options, isOutdoorTour: true')
        return true
      }
      
      // Otherwise check if outdoor_map is in the active navigation options (fallback)
      const result = this.activeNavigation.includes('outdoor_map')
      console.log('Using activeNavigation (outdoor_map), isOutdoorTour:', result)
      return result
    },
    
    // Filter points based on GameModule visibility conditions
    visiblePoints() {
      if (!this.fetchedPoints || Object.keys(this.fetchedPoints).length === 0) {
        return {}
      }
      
      // If GameModule is not active, show all points
      if (!moduleManager.isModuleActive('game')) {
        console.log('GameModule not active, showing all points')
        return this.fetchedPoints
      }
      
      // Check if GameModule is ready with required methods
      if (!moduleManager.isModuleReady('game', ['shouldShowPoint'])) {
        console.log('GameModule not ready, showing all points')
        return this.fetchedPoints
      }
      
      // Filter points based on GameModule visibility - re-evaluate conditions in real-time
      const visiblePoints = {}
      Object.entries(this.fetchedPoints).forEach(([tourPointId, tourPointData]) => {
        // Re-evaluate show conditions in real-time
        let shouldShow = true
        
        try {
          // Get the GameModule instance
          const gameModule = moduleManager.getModule('game')
          if (gameModule && typeof gameModule.shouldShowPoint === 'function') {
            shouldShow = gameModule.shouldShowPoint(tourPointData)
            console.log(`tourPoint ${tourPointId} shouldShow: ${shouldShow}`)
          } else {
            console.log(`GameModule not available or shouldShowPoint method not found for tourPoint ${tourPointId}`)
          }
        } catch (error) {
          console.error('Error evaluating show conditions for tourPoint', tourPointId, error)
          // Fallback: show point if condition evaluation fails
          shouldShow = true
        }
        
        if (shouldShow) {
          visiblePoints[tourPointId] = tourPointData
        }
      })
      
      console.log(`Filtered ${Object.keys(this.fetchedPoints).length} points to ${Object.keys(visiblePoints).length} visible points`)
      return visiblePoints
    }
  },
  data() {
    return {
      points: [],
      fetchedPoints: {}, // Match React data structure
      pointsLoading: true,
      localLoading: false, // Local loading state for this component
      gpsLockWatcher: null, // For watching GPS lock distance
      gpsLockedPoints: new Set(), // Track which points are currently locked
      permanentlyUnlockedPoints: new Set(), // Track points that have been unlocked and should stay unlocked (TODO should be checked via visitedPoints)
      backgroundGPSWatcher: null, // For background monitoring of all locked points
      _isBeingDestroyed: false, // Added to track component destruction
      pendingPointOpenings: [] // Queue for points that need to be opened after loading
    }
  },
  async mounted() {
    console.log('=== POINTS COMPONENT MOUNTED ===')
    console.log('Component name:', this.$options.name)
    console.log('Props received:', this.$props)
    console.log('pointId prop:', this.pointId)
    console.log('selectedView prop:', this.selectedView)
    console.log('Current route:', this.$route.path)
    console.log('Route params:', this.$route.params)
    
    // Initialize destruction flag
    this._isBeingDestroyed = false
    
    // Listen for game state changes to refresh point visibility
    moduleManager.addEventListener('game:state_updated', this.refreshPointVisibility.bind(this))
    moduleManager.addEventListener('game:inventory_updated', this.refreshPointVisibility.bind(this))
    
    console.log('Points component mounted and event listeners registered')
    
    // Make test method available globally for debugging
    window.testPointVisibility = this.testVisibilityUpdate.bind(this)
    window.testTourSettingsWatcher = this.testTourSettingsWatcher.bind(this)
    window.debugGameModule = () => {
      console.log('=== GLOBAL GAME MODULE DEBUG ===')
      console.log('ModuleManager state:')
      moduleManager.debugState()
      console.log('GameModule specific checks:')
      console.log('- isModuleActive:', moduleManager.isModuleActive('game'))
      console.log('- isModuleReady (basic):', moduleManager.isModuleReady('game'))
      console.log('- isModuleReady (with methods):', moduleManager.isModuleReady('game', ['processAllPoints', 'shouldShowPoint', 'shouldUnlockPoint']))
      console.log('- getModule result:', moduleManager.getModule('game'))
      console.log('- getModuleState:', moduleManager.getModuleState('game'))
      console.log('- getModuleData:', moduleManager.getModuleData('game'))
    }
    
    window.forceReEnableGameModule = () => {
      console.log('=== FORCE RE-ENABLE GAME MODULE ===')
      const result = moduleManager.forceReEnableModule('game')
      console.log('Re-enable result:', result)
      if (result) {
        console.log('GameModule re-enabled, testing visibility...')
        setTimeout(() => {
          this.testVisibilityUpdate()
        }, 100)
      }
      return result
    }
    
    // Add overlay debug methods
    window.debugOverlayState = this.debugOverlayState.bind(this)
    window.forceOverlayReset = this.forceOverlayReset.bind(this)
    window.debugPointOpening = this.debugPointOpening.bind(this)
    window.testOverlayWithVariables = () => {
      console.log('=== TEST OVERLAY WITH VARIABLES ===')
      // Force overlay reset first
      this.forceOverlayReset()
      
      // Then try to open Variables overlay
      setTimeout(() => {
        this.$store.dispatch('overlay/openOverlay', {
          component: markRaw({
            name: 'TestVariablesWrapper',
            render() {
              return h('div', { class: 'p-4' }, 'Test Variables Component')
            }
          }),
          title: 'Test Variables',
          props: {}
        })
        console.log('Test Variables overlay opened')
      }, 100)
    }
    
    // Add pending point openings debug method
    window.debugPendingPointOpenings = () => {
      console.log('=== DEBUG PENDING POINT OPENINGS ===')
      console.log('Pending point openings:', this.pendingPointOpenings)
      console.log('Fetched points count:', Object.keys(this.fetchedPoints).length)
      console.log('Fetched points IDs:', Object.keys(this.fetchedPoints))
    }
    
    console.log('=== VUE POINTS COMPONENT MOUNTED ===')
    console.log('Current framework:', localStorage.getItem('framework'))
    console.log('Component name:', this.$options.name)
    console.log('Tour settings available:', !!this.tourSettings)
    console.log('Active tour:', this.activeTour)
    console.log('Point ID from props:', this.pointId)
    
    // Initialize modules if tour settings are available
    if (this.tourSettings) {
      moduleManager.initializeModules(this.tourSettings)
      // Debug: Check module state after initialization
      setTimeout(() => {
        moduleManager.debugState()
      }, 100)
      // Only load points if we haven't already loaded them
      if (Object.keys(this.fetchedPoints).length === 0) {
        await this.loadPoints()
      }
    }
    
    // Start location watching if outdoor map is active
    if (this.currentDisplay === 'outdoor_map') {
      this.startLocationWatching()
    }
    
    // Start location watching if this is an outdoor tour (needed for GPS lock checks)
    if (this.isOutdoorTour) {
      console.log('Outdoor tour detected, starting location watching for GPS lock checks')
      this.startLocationWatching()
    }
    
    // Register for module events
    this.registerModuleEvents()

    // check the tourSettings if we have a point to open on start.
    const OpenTourPointOnstart = this.tourSettings?.start_point?.id;
    if (OpenTourPointOnstart) {
      // Check if this point has already been opened in this session
      const hasBeenOpened = this.$store.getters['visited/startPointVisited'];
      // hasBeenOpened is an array with tourId as key and boolean as value
      const tourId = this.activeTour;
      console.log('hasBeenOpened:', hasBeenOpened)
      console.log('tourId:', tourId)
      if (!hasBeenOpened[tourId]) {
        console.log('Opening point on start:', OpenTourPointOnstart);
        
        // Mark as opened in session storage
        hasBeenOpened[tourId] = true;
        console.log('hasBeenOpened after setting true:', hasBeenOpened)
        this.$store.dispatch('visited/setStartPointVisited', hasBeenOpened);
        
        // Open the tourpoint in overlay using the existing method
        this.$nextTick(() => {
          this.showPointFromId(OpenTourPointOnstart);
        });
      } else {
        console.log('Point already opened in this session:', OpenTourPointOnstart);
      }
    }


  },
  watch: {
    // Watch for tour settings changes to reload points
    tourSettings: {
      handler(newSettings, oldSettings) {
        console.log('Points - tourSettings changed:', { newSettings, oldSettings })
        
        // Only process if we have new settings and they're different from old settings
        if (newSettings && (!oldSettings || JSON.stringify(newSettings) !== JSON.stringify(oldSettings))) {
          console.log('Tour settings modules:', newSettings.modules)
          console.log('Game module in modules array:', newSettings.modules?.includes('game'))
          
          // Reinitialize modules with new settings
          moduleManager.initializeModules(newSettings)
          
          // Check if game module is now active
          setTimeout(() => {
            console.log('Game module active after initialization:', moduleManager.isModuleActive('game'))
            moduleManager.debugState()
            
            // If GameModule is now active, reprocess points after a delay
            if (moduleManager.isModuleActive('game')) {
              setTimeout(() => {
                this.reprocessPointsWithGameModule()
              }, 500)
            }
          }, 100)
          
          // Only load points if we haven't already loaded them or if settings changed significantly
          if (Object.keys(this.fetchedPoints).length === 0 || 
              !oldSettings || 
              oldSettings.id !== newSettings.id) {
            this.loadPoints()
          }
          
          // Also initialize navigation if not already done
          if (!this.pointNavigation) {
            this.initializePointNavigation(newSettings)
          }
        }
      },
      immediate: false // Changed from true to false to prevent immediate triggering
    },

    isOverlayVisible: {
      handler(newVal) {
        console.log('=== WATCH OVERLAY VISIBLE ===')
        console.log('New value:', newVal)
        if (!newVal) {
          // only change the url, but do not redirect or reload the component when the component is already loaded
          window.history.pushState({}, '', `/${this.$route.params.tourId}/points${window.location.search}`);
        }
      }
    },
    
    // Watch for active navigation changes to update display
    activeNavigation: {
      handler(newActiveNavigation, oldActiveNavigation) {
        // Only process if navigation actually changed
        if (newActiveNavigation && 
            (!oldActiveNavigation || JSON.stringify(newActiveNavigation) !== JSON.stringify(oldActiveNavigation))) {
          if (newActiveNavigation.length > 0 && !this.pointNavigation) {
            this.initializePointNavigation(this.tourSettings)
          }
        }
      },
      immediate: false // Changed from true to false
    },
    
    // Watch for point navigation changes
    pointNavigation: {
      handler(newNavigation, oldNavigation) {
        // Only process if navigation actually changed
        if (newNavigation !== oldNavigation) {
          console.log('Point navigation changed:', { newNavigation, oldNavigation })
        }
      },
      immediate: false // Changed from true to false
    },
    
    // Watch for current display changes to start/stop location watching
    currentDisplay: {
      handler(newDisplay, oldDisplay) {
        // Only process if display actually changed
        if (newDisplay !== oldDisplay) {
          if (newDisplay === 'outdoor_map') {
            // Start location watching when outdoor map is active
            this.startLocationWatching()
          } else if (!this.isOutdoorTour) {
            // Stop location watching when not using outdoor map AND not in an outdoor tour
            this.stopLocationWatching()
          }
          // If we're in an outdoor tour but not on outdoor map, keep location watching for GPS lock checks
        }
      },
      immediate: false // Changed from true to false
    },
    
    // Watch for pointId prop changes to show overlay
    pointId: {
      handler(newPointId, oldPointId) {
        console.log('=== POINT ID WATCHER TRIGGERED ===')
        console.log('New pointId:', newPointId)
        console.log('Old pointId:', oldPointId)
        console.log('Fetched points count:', Object.keys(this.fetchedPoints).length)
        console.log('Fetched points IDs:', Object.keys(this.fetchedPoints))
        console.log('Pending openings:', this.pendingPointOpenings)
        
        // Only process if pointId actually changed
        if (newPointId && newPointId !== oldPointId) {
          console.log('Points - pointId prop changed:', newPointId)
          
          // Check if points have been loaded
          if (Object.keys(this.fetchedPoints).length === 0) {
            console.log('Points not loaded yet, queuing point opening:', newPointId)
            this.pendingPointOpenings.push(newPointId)
          } else {
            console.log('Points already loaded, showing point immediately:', newPointId)
            this.showPointFromId(newPointId)
          }
        } else {
          console.log('PointId unchanged or empty, skipping')
        }
      },
      immediate: true // Changed to true to trigger on mount
    },
    
    // Watch for outdoor tour changes to start/stop location watching
    isOutdoorTour: {
      handler(isOutdoor, wasOutdoor) {
        // Only process if outdoor status actually changed
        if (isOutdoor !== wasOutdoor) {
          console.log('=== OUTDOOR TOUR CHANGED ===')
          console.log('Is outdoor tour:', isOutdoor)
          
          if (isOutdoor) {
            console.log('Starting location watching for outdoor tour GPS lock checks')
            this.startLocationWatching()
          } else {
            console.log('Stopping location watching - no longer an outdoor tour')
            this.stopLocationWatching()
          }
        }
      },
      immediate: false // Changed from true to false
    },
    
    // Watch for game state changes to trigger visibility updates
    gameState: {
      handler(newGameState, oldGameState) {
        console.log('=== GAME STATE CHANGED ===')
        console.log('Old game state:', oldGameState)
        console.log('New game state:', newGameState)
        
        // Check if component is being destroyed
        if (this._isBeingDestroyed) {
          console.log('Component is being destroyed, skipping game state update')
          return
        }
        
        if (newGameState && moduleManager.isModuleActive('game')) {
          console.log('Game state changed, triggering visibility refresh')
          this.$nextTick(() => {
            // Double-check component is still mounted before refreshing
            if (!this._isBeingDestroyed) {
              this.refreshPointVisibility()
            } else {
              console.log('Component destroyed during nextTick, skipping refresh')
            }
          })
        }
      },
      deep: true,
      immediate: false
    },
    
    // Watch for visiblePoints changes to trigger map updates
    visiblePoints: {
      handler(newVisiblePoints, oldVisiblePoints) {
        console.log('=== VISIBLE POINTS CHANGED ===')
        console.log('Old visible points count:', Object.keys(oldVisiblePoints || {}).length)
        console.log('New visible points count:', Object.keys(newVisiblePoints || {}).length)
        console.log('Old visible point IDs:', Object.keys(oldVisiblePoints || {}))
        console.log('New visible point IDs:', Object.keys(newVisiblePoints || {}))
        
        // Force a reactivity update to ensure the map component receives the new points
        this.$nextTick(() => {
          if (!this._isBeingDestroyed) {
            console.log('Forcing reactivity update for visible points change')
            this.$forceUpdate()
          }
        })
      },
      deep: true,
      immediate: false
    },
    
    // Watch for fetchedPoints changes to process pending point openings
    fetchedPoints: {
      handler(newFetchedPoints, oldFetchedPoints) {
        console.log('=== FETCHED POINTS CHANGED ===')
        console.log('Old fetched points count:', Object.keys(oldFetchedPoints || {}).length)
        console.log('New fetched points count:', Object.keys(newFetchedPoints || {}).length)
        
        // If we now have points and there are pending openings, process them
        if (Object.keys(newFetchedPoints || {}).length > 0 && this.pendingPointOpenings.length > 0) {
          console.log('Points loaded and pending openings exist, processing...')
          this.$nextTick(() => {
            this.processPendingPointOpenings()
          })
        }
      },
      deep: true,
      immediate: false
    }
  },
  methods: {
    ...mapActions('settings', ['loadTourSettings']),
    ...mapActions('pointNavigation', ['switchPointNavigation', 'initializeNavigation']),
    ...mapActions('pointPreview', ['closePreview']),
    ...mapActions('location', ['startLocationWatching', 'stopLocationWatching']),
    
    // Initialize point navigation based on tour settings
    initializePointNavigation(settings) {
      if (!settings?.navigation) return
      
      this.initializeNavigation(settings)
    },
    
    showMultiLingualText(text, altText = null, showFirstLanguage = false) {
      return showMultiLingualText(text, altText, showFirstLanguage, this.$i18n.locale)
    },

    // GPS Lock helper methods
    isPointGPSLocked(pointId) {
      // If point visited, it should not lock again

      if (this.permanentlyUnlockedPoints.has(pointId)) {
        return false
      }
      
      // Otherwise check current GPS lock status
      return this.gpsLockedPoints.has(pointId)
    },
    
    getGPSLockedPoints() {
      return Object.values(this.fetchedPoints).filter(point => {
        const pointId = point?.point?.id || point?.data?.point?.id || point?.id
        return pointId && this.isPointGPSLocked(pointId)
      })
    },
    
    async loadPoints() {
      console.log('=== LOAD POINTS ===')
      console.log('Tour settings:', this.tourSettings)
      console.log('Active tour:', this.activeTour)
      
      // Prevent multiple simultaneous calls
      if (this.localLoading) {
        console.log('Points already loading, skipping duplicate call')
        return
      }
      
      if (!this.tourSettings) {
        console.log('No tour settings available')
        return
      }
      
      this.localLoading = true
      
      try {
        // Load tourPoints from the store
        const tourPointsResponse = await this.$store.dispatch('tours/loadTours', this.activeTour)
        
        if (tourPointsResponse && Array.isArray(tourPointsResponse)) {
          console.log('Tour points loaded:', tourPointsResponse)
          
          // Transform the response to match expected structure
          const transformedPoints = {}
          tourPointsResponse.forEach((data) => {
            let point = {}
            point.data = data
            point.state = (data.settings?.gps_lock) ? 'locked' : 'default'
            point.locked = !!data.settings?.gps_lock
            transformedPoints[data.id] = point
          })
          
          // Process points through GameModule if it's active
          if (moduleManager.isModuleActive('game')) {
            console.log('GameModule is active, processing points for visibility and unlock status')
            
            // Check if GameModule is ready with required methods
            if (moduleManager.isModuleReady('game', ['processAllPoints', 'shouldShowPoint', 'shouldUnlockPoint'])) {
              console.log('GameModule is ready, processing points')
              
              const gameModule = moduleManager.getModule('game')
              const processedPoints = gameModule.processAllPoints(transformedPoints)
              
              console.log('Points processed by GameModule:', processedPoints)
              this.fetchedPoints = processedPoints
            } else {
              console.log('GameModule not ready, waiting for initialization...')
              
              // Wait for GameModule to be ready with retry mechanism
              let retryCount = 0
              const maxRetries = 5
              const retryDelay = 200
              
              const waitForGameModule = async () => {
                if (moduleManager.isModuleReady('game', ['processAllPoints', 'shouldShowPoint', 'shouldUnlockPoint'])) {
                  console.log('GameModule is now ready, processing points')
                  
                  const gameModule = moduleManager.getModule('game')
                  const processedPoints = gameModule.processAllPoints(transformedPoints)
                  
                  console.log('Points processed by GameModule:', processedPoints)
                  this.fetchedPoints = processedPoints
                } else if (retryCount < maxRetries) {
                  console.log(`GameModule not ready, retry ${retryCount + 1}/${maxRetries}`)
                  retryCount++
                  await new Promise(resolve => setTimeout(resolve, retryDelay))
                  await waitForGameModule()
                } else {
                  console.log('GameModule not ready after retries, using points as-is')
                  this.fetchedPoints = transformedPoints
                }
              }
              
              await waitForGameModule()
            }
          } else {
            console.log('GameModule not active, using points as-is')
            this.fetchedPoints = transformedPoints
          }
          
          // Initialize GPS lock status after points are loaded
          this.$nextTick(() => {
            this.initializeGPSLockStatus()
          })
          
          // Process any pending point openings after points are loaded
          if (this.pendingPointOpenings.length > 0) {
            console.log('Processing pending point openings after points loaded')
            this.$nextTick(() => {
              this.processPendingPointOpenings()
            })
          }
        } else {
          console.log('No points data returned')
          this.fetchedPoints = {}
        }
        
      } catch (error) {
        console.error('Error loading points:', error)
        this.fetchedPoints = {}
      } finally {
        this.localLoading = false
      }
    },
    
    // Handle item click (mirrors React handleItemClick)
    handleItemClick(pointClicked, showPreview = true) {
      console.log('=== POINTS HANDLE ITEM CLICK ===')
      console.log('Point clicked:', pointClicked)
      console.log('Show preview parameter:', showPreview)
      console.log('Current display:', this.currentDisplay)
      
      // Handle different data structures
      let pointData = pointClicked
      let pointId = null
      
      if (pointClicked?.point?.id) {
        // React-style structure: { point: { id: ... }, data: ... }
        pointId = pointClicked.point.id
        pointData = pointClicked
        console.log('Using React-style structure, pointId:', pointId)
      } else if (pointClicked?.data?.point?.id) {
        // Vue structure: { data: { point: { id: ... }, ... } }
        pointId = pointClicked.data.point.id
        pointData = pointClicked
        console.log('Using Vue structure, pointId:', pointId)
      } else if (pointClicked?.id) {
        // Direct point structure: { id: ... }
        pointId = pointClicked.id
        pointData = { point: { id: pointId }, data: pointClicked }
        console.log('Using direct point structure, pointId:', pointId)
      }
      
      console.log('Processed pointData:', pointData)
      console.log('Extracted pointId:', pointId)
      
      // Check if this is an outdoor tour (outdoor_map navigation available)
      console.log('Is outdoor tour:', this.isOutdoorTour)
      console.log('Active navigation options:', this.activeNavigation)
      
      // Only check GPS lock for outdoor tours
      if (this.isOutdoorTour && (pointData?.settings?.gps_lock || pointData?.data?.settings?.gps_lock)) {
        console.log('Outdoor tour with GPS lock enabled, checking distance')
        this.checkGPSLockAndProceed(pointData, pointId)
        return
      }
      
      // For indoor tours or points without GPS lock, proceed directly
      if (pointData) {
        console.log('Indoor tour or no GPS lock, proceeding directly')
        this.navigateToPoint(pointData)
      } else {
        console.error('No valid pointData found after processing')
      }
    },
    
    checkGPSLockAndProceed(pointData, pointId) {
      console.log('=== CHECK GPS LOCK AND PROCEED ===')
      console.log('Point data:', pointData)
      console.log('Point ID:', pointId)
      
      // Check if point has GPS location (try both possible locations)
      const gpsLocation = pointData?.gps_location || pointData?.data?.gps_location
      if (!gpsLocation) {
        console.log('Point has no GPS location')
        // Show modal for missing GPS location
        this.$store.dispatch('modal/openModal', {
          title: 'Punt vereist GPS locatie',
          message: 'Dit punt vereist GPS locatie, maar er zijn geen locatiegegevens beschikbaar.',
          type: 'default'
        })
        return
      }
      
      // Show modal immediately and start watching distance
      this.showGPSLockModalWithDistanceWatch(pointData, pointId)
    },

    getGpsRadius() {
      return this.tourSettings?.navigation?.google_maps?.gps_radius || 10
    },
    
    showGPSLockModalWithDistanceWatch(pointData, pointId) {
      console.log('=== SHOW GPS LOCK MODAL WITH DISTANCE WATCH ===')
      
      // Get GPS location and settings from the correct location in the data structure
      const pointLocation = pointData?.gps_location || pointData?.data?.gps_location
      const maxDistance = this.getGpsRadius()
      
      console.log('Point location:', pointLocation)
      console.log('Max distance:', maxDistance)
      
      // Create a watcher function that updates the modal
      const updateModalContent = () => {
        // Check if we have current location
        if (!this.currentLocation || !this.isValidLocation) {
          const message = 'Geen GPS signaal beschikbaar. Schakel locatieservices in en probeer het opnieuw.'
          this.$store.dispatch('modal/openModal', {
            title: 'Punt vereist GPS locatie',
            message: message,
            type: 'default'
          })
          return false // Continue watching
        }
        
        // Parse GPS location - handle different formats
        let pointLat, pointLng
        if (typeof pointLocation === 'string') {
          // Format: "lat,lng"
          const coords = pointLocation.split(',').map(parseFloat)
          pointLat = coords[0]
          pointLng = coords[1]
        } else if (pointLocation && typeof pointLocation === 'object') {
          // Format: { latitude: lat, longitude: lng } or { lat: lat, lng: lng }
          pointLat = pointLocation.latitude || pointLocation.lat
          pointLng = pointLocation.longitude || pointLocation.lng
        } else {
          console.error('Invalid GPS location format:', pointLocation)
          const message = 'Ongeldige GPS locatiegegevens voor dit punt.'
          this.$store.dispatch('modal/openModal', {
            title: 'Punt vereist GPS locatie',
            message: message,
            type: 'default'
          })
          return false // Continue watching
        }
        
        // Get current location with proper format handling
        const currentLat = this.currentLocation?.latitude || this.currentLocation?.lat
        const currentLng = this.currentLocation?.longitude || this.currentLocation?.lng
        
        // Validate coordinates
        if (isNaN(pointLat) || isNaN(pointLng) || isNaN(currentLat) || isNaN(currentLng)) {
          console.error('Invalid coordinates detected:', {
            pointLat, pointLng,
            currentLat, currentLng
          })
          const message = 'Unable to calculate distance due to invalid GPS coordinates.'
          this.$store.dispatch('modal/openModal', {
            title: 'Point Out of Range',
            message: message,
            type: 'default'
          })
          return false // Continue watching
        }
        
        // Calculate current distance
        const distance = this.calculateDistance(currentLat, currentLng, pointLat, pointLng)
        
        console.log('Current distance to point:', distance, 'meters')
        
        // Check if distance calculation failed
        if (isNaN(distance)) {
          console.error('Distance calculation returned NaN')
          const message = 'Kan afstand niet berekenen naar dit punt.'
          this.$store.dispatch('modal/openModal', {
            title: 'Punt vereist GPS locatie',
            message: message,
            type: 'default'
          })
          return false // Continue watching
        }
        
        // Check if within range
        if (!this.isPointGPSLocked(pointId)) {
          console.log('Point is now within range, closing modal and opening point')
          this.$store.dispatch('modal/closeModal')
          
          // Stop the watcher
          if (this.gpsLockWatcher) {
            this.gpsLockWatcher()
            this.gpsLockWatcher = null
          }
          
          // Open the point
          this.navigateToPoint(pointData)
          return true // Stop watching
        } else {
          // Update modal with current distance
          const message = `Dit punt is buiten bereik. Je moet binnen ${maxDistance} meter van het punt zijn om het te openen.<br><br>Huidige afstand: ${Math.round(distance)} meter.`
          this.$store.dispatch('modal/openModal', {
            title: 'Punt buiten bereik',
            message: message,
            type: 'default'
          })
          return false // Continue watching
        }
      }
      
      // Show initial modal
      updateModalContent()
      
      // Set up watcher for location changes
      this.gpsLockWatcher = this.$watch(
        () => [this.currentLocation, this.isValidLocation],
        () => {
          const shouldStop = updateModalContent()
          if (shouldStop) {
            // Watcher will be cleaned up in updateModalContent
            return
          }
        },
        { deep: true, immediate: false }
      )
      
      // Also watch for modal close to stop the watcher
      const modalCloseWatcher = this.$watch(
        () => this.$store.getters['modal/isModalOpen'],
        (isOpen) => {
          if (!isOpen && this.gpsLockWatcher) {
            console.log('Modal closed manually, stopping GPS lock watcher')
            this.gpsLockWatcher()
            this.gpsLockWatcher = null
            modalCloseWatcher() // Clean up this watcher too
          }
        }
      )
    },
    
    calculateDistance(lat1, lon1, lat2, lon2) {
      console.log('=== CALCULATE DISTANCE ===')
      console.log('Lat1:', lat1)
      console.log('Lon1:', lon1)
      console.log('Lat2:', lat2)
      console.log('Lon2:', lon2)
      
      // Haversine formula to calculate distance between two points
      const R = 6371e3 // Earth's radius in meters
      const φ1 = lat1 * Math.PI / 180
      const φ2 = lat2 * Math.PI / 180
      const Δφ = (lat2 - lat1) * Math.PI / 180
      const Δλ = (lon2 - lon1) * Math.PI / 180
      
      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      
      const distance = R * c
      console.log('Calculated distance:', distance, 'meters')
      return distance
    },
    
    // Open preview (mirrors React openPreview)
    openPreview(point) {
      console.log('=== OPEN PREVIEW ===')
      console.log('Point to preview:', point)
      console.log('Current preview state - isPreviewOpen:', this.isPreviewOpen)
      console.log('Current preview state - previewPoint:', this.previewPoint)
      
      this.$store.dispatch('pointPreview/openPreview', point)
      
      console.log('Preview action dispatched to store')
    },
    
    // Handle preview action (when user clicks on preview)
    handlePreviewAction(point) {
      console.log('=== HANDLE PREVIEW ACTION ===')
      console.log('Preview action triggered for point:', point)
      console.log('Closing preview and showing point')
      
      // Get the point ID and use the proper flow
      let pointId = null
      if (point?.point?.id) {
        pointId = point.point.id
      } else if (point?.data?.point?.id) {
        pointId = point.data.point.id
      } else if (point?.id) {
        pointId = point.id
      }
      
      if (pointId) {
        this.showPointFromId(pointId)
      } else {
        console.error('No point ID found in point data:', point)
      }
      
      this.closePreview()
    },
    
    // Handle navigation action (placeholder for navigation functionality)
    handleNavigationAction(point) {
      console.log('=== HANDLE NAVIGATION ACTION ===')
      console.log('Navigation action triggered for point:', point)
      // This would integrate with navigation functionality
      // For now, just show the point
      
      // Get the point ID and use the proper flow
      let pointId = null
      if (point?.point?.id) {
        pointId = point.point.id
      } else if (point?.data?.point?.id) {
        pointId = point.data.point.id
      } else if (point?.id) {
        pointId = point.id
      }
      
      if (pointId) {
        this.showPointFromId(pointId)
      } else {
        console.error('No point ID found in point data:', point)
      }
      
      this.closePreview()
    },
    
    showPoint(point, tourPointId = null) {
      console.log('=== SHOW POINT ===')
      console.log('Point to show:', point)
      console.log('Tour point ID:', tourPointId)
      
      // Debug overlay state before opening
      this.debugOverlayState()
      
      // If no tourPointId provided, try to extract it from point data
      if (!tourPointId) {
        // Get the point ID from the point data
        let pointId = null
        if (point?.point?.id) {
          pointId = point.point.id
          console.log('Extracted pointId from point.point.id:', pointId)
        } else if (point?.data?.point?.id) {
          pointId = point.data.point.id
          console.log('Extracted pointId from point.data.point.id:', pointId)
        } else if (point?.id) {
          pointId = point.id
          console.log('Extracted pointId from point.id:', pointId)
        }
        
        console.log('Final extracted pointId:', pointId)
        
        if (!pointId) {
          console.error('No point ID found in point data:', point)
          return
        }
        
        tourPointId = pointId
      }
      
      console.log('Creating TourPoint component with tourPointId:', tourPointId)
      
      // Import TourPoint component dynamically
      import('./TourPoint.vue').then(({ default: TourPointComponent }) => {
        try {
          // Create a wrapper component that renders TourPoint
          const TourPointWrapper = {
            name: 'TourPointWrapper',
            components: { TourPointComponent },
            props: ['tourPointId'],
            setup(props) {
              return () => h(TourPointComponent, { tourPointId: props.tourPointId })
            }
          }
          
          console.log('TourPoint component created, setting overlay')
          
          // Ensure overlay state is clean before opening
          this.$nextTick(() => {
            // Show TourPoint in overlay using Vuex store
            this.$store.dispatch('overlay/openOverlay', {
              component: markRaw(TourPointWrapper),
              title: null,
              props: { tourPointId }
            })
            
            console.log('Overlay opened via Vuex store')
            
            // Debug overlay state after opening
            this.$nextTick(() => {
              this.debugOverlayState()
            })
          })
        } catch (error) {
          console.error('Error creating TourPoint wrapper:', error)
        }
      }).catch(error => {
        console.error('Failed to load TourPoint component:', error)
      })
    },
    
    navigateToPoint(point) {
      console.log('=== NAVIGATE TO POINT ===')
      console.log('Point to navigate to:', point)
      
      // Get the point ID from the point data
      let pointId = null
      if (point?.point?.id) {
        pointId = point.point.id
      } else if (point?.data?.point?.id) {
        pointId = point.data.point.id
      } else if (point?.id) {
        pointId = point.id
      }
      
      if (!pointId) {
        console.error('No point ID found in point data:', point)
        return
      }
      
      console.log('Navigating to point URL:', pointId)
      
     this.showPointFromId(pointId)

     // only replace the url, but do not redirect or reload the component when the component is already loaded
     window.history.pushState({}, '', `/${this.$route.params.tourId}/point/${pointId}${window.location.search}`);
   },
    
    showPointFromId(pointId) {
      console.log('=== SHOW POINT FROM ID ===')
      console.log('Point ID:', pointId)

      // We can have multiple tourPoints attached to the same point. we should check all conditions and show the first match by tourPoint sortorder
      // Find the point in the fetchedPoints object
      console.log('Fetched tourPoints:', this.fetchedPoints)
      console.log('Fetched tourPoints keys:', Object.keys(this.fetchedPoints))
      // find all tourpoints for the pointId

      const tourPoints = Object.values(this.fetchedPoints || {}).filter(tp => {
        const pid = tp?.data?.point?.id || tp?.point?.id || tp?.id
        return String(pid) === String(pointId)
      })
      console.log('Tour points:', tourPoints)

      // compare tourPoints against visiblePoints. if the tourPoint is not in visiblePoints, filter it out
      const visiblePoints = Object.values(this.visiblePoints || {}).filter(vp => {
        const pid = vp?.data?.point?.id || vp?.point?.id || vp?.id
        return String(pid) === String(pointId)
      })
      console.log('Visible points:', visiblePoints)
      const visibleTourPoints = tourPoints.filter(tp => visiblePoints.some(vp => vp.data.id === tp.data.id))
      console.log('Tour points in visible points:', visibleTourPoints)


      // find the tourPoint with the lowest sortorder
      let tourPoint = null;
      if (visibleTourPoints.length > 0) {
        tourPoint = visibleTourPoints.sort((a, b) => a.data.order - b.data.order)[0]
        console.log('tourPoint:', tourPoint)
        console.log('tourPoint -> point:', tourPoint?.data?.point)
      } else {
        console.log('No tour points found in visible points')
      }
    
      if (tourPoint) {
        console.log('tourPoint found, loading and showing')
        this.loadAndShowTourPoint(tourPoint, pointId)
      } else {
        console.log('Point not found in fetchedPoints, loading directly')
        // If point is not in fetchedPoints, load it directly via API
        this.loadAndShowPoint(pointId)
      }
    },
    
    async loadAndShowPoint(pointId) {
      console.log('=== LOAD AND SHOW POINT ===')
      console.log('Loading point directly:', pointId)
      
      try {
        const activeTour = this.$store.getters['tours/activeTour']
        const response = await this.$store.dispatch('tours/loadPoint', { pointId, activeTour })
        
        if (response && response.data) {
          console.log('Point loaded successfully:', response.data)
          
          // Create a point object in the expected format
          const pointData = {
            point: { id: response.data.id },
            data: response.data
          }
          
          this.loadAndShowTourPoint(pointData, pointId)
        } else {
          console.error('Failed to load point data')
        }
      } catch (error) {
        console.error('Error loading point:', error)
      }
    },
    
    async loadAndShowTourPoint(pointData, pointId) {
      console.log('=== LOAD AND SHOW TOUR POINT ===')
      console.log('Point data:', pointData)
      console.log('Point ID:', pointId)
      console.log('Point data keys:', Object.keys(pointData))
      console.log('Point data.data keys:', pointData.data ? Object.keys(pointData.data) : 'no data')
      console.log('Point data.data.tour_points:', pointData.data?.tour_points)
      console.log('Point data.data.tour_points type:', typeof pointData.data?.tour_points)
      console.log('Point data.data.tour_points is array:', Array.isArray(pointData.data?.tour_points))
      console.log('Point data.data.tour_points length:', pointData.data?.tour_points?.length)
      
      try {
        const activeTour = this.$store.getters['tours/activeTour']
       
        console.log('Active tour:', activeTour)
        
        // Check if pointData already contains tour point data (direct TourPoint data)
        if (pointData.conditions !== undefined || pointData.actions !== undefined) {
          console.log('Point data already contains TourPoint data, using directly')
          this.checkTourPointConditionsAndShow(pointData, pointData)
          return
        }
        
        // Check if pointData.data already contains tour point data (direct TourPoint data)
        if (pointData.data?.conditions !== undefined || pointData.data?.actions !== undefined) {
          console.log('Point data.data already contains TourPoint data, using directly')
          this.checkTourPointConditionsAndShow(pointData.data, pointData)
          return
        }
        
        // Check if pointData.data contains unlock_conditions, visibility_conditions, or actions (tour point data)
        if (pointData.data?.unlock_conditions !== undefined || 
            pointData.data?.visibility_conditions !== undefined || 
            pointData.data?.actions !== undefined) {
          console.log('Point data.data contains tour point data (unlock_conditions/visibility_conditions/actions), using directly')
          this.checkTourPointConditionsAndShow(pointData.data, pointData)
          return
        }
        
        // Find the tour point for the active tour from pointData
        let tourPointData = null
        
        // Handle different possible tour_points structures
        if (Array.isArray(pointData.data?.tour_points)) {
          console.log('tour_points is an array, searching...')
          tourPointData = pointData.data.tour_points.find(tp => {
            console.log('Checking tour point:', tp)
            console.log('Tour point tour ID:', tp?.tour?.id)
            console.log('Active tour:', activeTour)
            return tp?.tour?.id === activeTour
          })
        } else if (typeof pointData.data?.tour_points === 'object' && pointData.data?.tour_points !== null) {
          console.log('tour_points is an object, searching...')
          tourPointData = Object.values(pointData.data.tour_points).find(tp => {
            console.log('Checking tour point:', tp)
            console.log('Tour point tour ID:', tp?.tour?.id)
            console.log('Active tour:', activeTour)
            return tp?.tour?.id === activeTour
          })
        }
        
        if (!tourPointData) {
          console.error('No tour point found for active tour:', activeTour)
          console.error('Available tour points:', pointData.data?.tour_points)
          return
        }
        
        console.log('Found tour point data:', tourPointData)
        
        // Check if tourPointData already has the detailed data we need
        if (tourPointData.conditions !== undefined || tourPointData.actions !== undefined) {
          console.log('Tour point data already has conditions/actions, using directly')
          this.checkTourPointConditionsAndShow(tourPointData, pointData)
          return
        }
        
        // Fetch the detailed tour point data from the API
        console.log('Fetching detailed tour point data from API...')
        const tourPointResponse = await this.$store.dispatch('tours/loadTourPoint', { 
          tourPointId: tourPointData.id, 
          activeTour, 
          language: this.$i18n.locale 
        })
        
        if (tourPointResponse && tourPointResponse.data) {
          console.log('Tour point API response:', tourPointResponse.data)
          console.log('Conditions in tour point data:', tourPointResponse.data.conditions)
          console.log('Actions in tour point data:', tourPointResponse.data.actions)
          
          // Check conditions using GameModule
          this.checkTourPointConditionsAndShow(tourPointResponse.data, pointData)
        } else {
          console.error('No tour point data returned from API')
          // Fallback to basic data
          this.checkTourPointConditionsAndShow(tourPointData, pointData)
        }
      } catch (error) {
        console.error('Error fetching tour point details:', error)
        // Fallback to basic data
        this.checkTourPointConditionsAndShow(pointData, pointData)
      }
    },
    
    checkTourPointConditionsAndShow(tourPointData, pointData) {
      console.log('=== CHECK TOUR POINT CONDITIONS AND SHOW ===')
      console.log('Tour point data:', tourPointData)
      console.log('Point data:', pointData)
      
      // Check if GameModule is active and ready
      if (moduleManager.isModuleReady('game', ['shouldUnlockPoint', 'getUnmetConditions'])) {
        console.log('GameModule is ready, checking unlock conditions')
        
        // Import GameModule and check unlock conditions
        import('../modules/GameModule.js').then(({ default: gameModule }) => {
          console.log('GameModule imported, checking unlock conditions')
          
          // Check if point should be unlocked (not just visible)
          const shouldUnlock = gameModule.shouldUnlockPoint(tourPointData)
          console.log('Point should be unlocked:', shouldUnlock)
          
          if (shouldUnlock) {
            console.log('Point unlocked - allowing access')
            // Unlock conditions met, allow access - show the point
            this.showPoint(pointData, tourPointData.id)
          } else {
            console.log('Point locked - unlock conditions not met')
            
            // Get unmet unlock conditions for the lock message
            const unmetConditions = gameModule.getUnmetConditions(tourPointData, 'unlock')
            console.log('Unmet unlock conditions:', unmetConditions)
            
            // Show modal with structured data instead of formatted message
            this.showLockedPointModal(
              '',
              unmetConditions, 
              moduleManager.getModuleData('game')
            )
          }
        }).catch(error => {
          console.error('Error importing GameModule:', error)
          // Fallback: allow access if GameModule import fails
          this.showPoint(pointData, tourPointData.id)
        })
      } else {
        console.log('GameModule is not ready, allowing access')
        console.log('GameModule state:', moduleManager.getModuleState('game'))
        console.log('Active modules:', moduleManager.getActiveModules())
        // Fallback: allow access if GameModule is not ready
        this.showPoint(pointData, tourPointData.id)
      }
    },
    
    showLockedPointModal(message, unmetConditions = null, gameState = null) {
      console.log('=== SHOW LOCKED POINT MODAL ===')
      console.log('Message:', message)
      console.log('Unmet conditions:', unmetConditions)
      console.log('Game state:', gameState)
      
      // Show modal with locked point message and structured data
      this.$store.dispatch('modal/openModal', {
        title: 'Punt vergrendeld',
        message: message,
        type: 'locked_point',
        data: {
          unmetConditions: unmetConditions,
          gameState: gameState
        }
      })
      
      // Listen for modal close to navigate back to points
      this.$nextTick(() => {
        // Watch for modal close
        const unwatch = this.$watch(
          () => this.$store.getters['modal/isModalOpen'],
          (isOpen) => {
            if (!isOpen) {
              console.log('Modal closed, navigating back to points')
              // Navigate back to points page
              this.$router.replace({ name: 'points', params: { tourId: this.$route.params.tourId }, query: { ...this.$route.query } })
              
              // Stop watching
              unwatch()
            }
          }
        )
      })
    },
    
    // Register for module events
    registerModuleEvents() {
      // Listen for game module events
      moduleManager.addEventListener('game:point_locked', this.handlePointLocked.bind(this))
      moduleManager.addEventListener('game:state_updated', this.handleGameStateUpdated.bind(this))
      moduleManager.addEventListener('game:level_up', this.handleLevelUp.bind(this))
      moduleManager.addEventListener('game:request_points_data', this.handleRequestPointsData.bind(this))
      
      // Listen for routing module events
      moduleManager.addEventListener('routing:navigation_enabled', this.handleNavigationEnabled.bind(this))
      moduleManager.addEventListener('routing:navigation_started', this.handleNavigationStarted.bind(this))
      moduleManager.addEventListener('routing:navigation_completed', this.handleNavigationCompleted.bind(this))
      
      // Ensure GameModule event listeners are registered
      this.ensureGameModuleListeners()
    },
    
    // Ensure GameModule event listeners are registered
    ensureGameModuleListeners() {
      console.log('[Points] Ensuring GameModule event listeners are registered')
      
      // Check if GameModule is ready
      if (!moduleManager.isModuleReady('game', ['handlePointOpening'])) {
        console.log('[Points] GameModule is not ready, cannot register listeners')
        return
      }
      
      // Manually register point:opening listener if it doesn't exist
      const existingListeners = moduleManager.eventListeners?.get('point:opening') || []
      const hasGameListener = existingListeners.some(listener => listener.moduleName === 'game')
      
      if (!hasGameListener) {
        console.log('[Points] GameModule point:opening listener not found, registering manually')
        
        // Import and register the GameModule listener
        import('../modules/GameModule.js').then(({ default: gameModule }) => {
          if (gameModule && gameModule.handlePointOpening) {
            moduleManager.addEventListener('point:opening', gameModule.handlePointOpening.bind(gameModule), 'game')
            console.log('[Points] GameModule point:opening listener registered manually')
          }
        }).catch(error => {
          console.error('[Points] Error importing GameModule:', error)
        })
      } else {
        console.log('[Points] GameModule point:opening listener already exists')
      }
    },
    
    // Handle point locked by game module
    handlePointLocked(data) {
      console.log('[Points] Point locked by game module:', data)
      
      // Show lock message to user
      this.$store.dispatch('overlay/showMessage', {
        title: 'Point Locked',
        message: data.message,
        type: 'warning'
      })
    },
    
    // Handle game state updates
    handleGameStateUpdated(gameState) {
      console.log('[Points] Game state updated:', gameState)
      
      // Update UI to reflect game state
      // This could update point visibility, scores, etc.
    },
    
    // Handle level up
    handleLevelUp(data) {
      console.log('[Points] Level up!', data)
      
      // Show level up celebration
      this.$store.dispatch('overlay/showMessage', {
        title: 'Level Up!',
        message: `Congratulations! You've reached level ${data.newLevel}!`,
        type: 'success'
      })
    },
    
    // Handle request for points data from GameModule
    handleRequestPointsData(data) {
      console.log('[Points] GameModule requested points data')
      
      if (data.callback && typeof data.callback === 'function') {
        data.callback(this.fetchedPoints)
      }
    },

    // Handle navigation enabled
    handleNavigationEnabled(data) {
      console.log('[Points] Navigation enabled for point:', data)
      
      // Enable navigation features in the UI
      // This could show navigation buttons, route information, etc.
    },
    
    // Handle navigation started
    handleNavigationStarted(data) {
      console.log('[Points] Navigation started:', data)
      
      // Show navigation UI
      // This could show a navigation overlay, route information, etc.
    },
    
    // Handle navigation completed
    handleNavigationCompleted(data) {
      console.log('[Points] Navigation completed:', data)
      
      // Hide navigation UI and show completion message
      this.$store.dispatch('overlay/showMessage', {
        title: 'Navigation Complete',
        message: 'You have reached your destination!',
        type: 'success'
      })
    },
    
    // GPS Lock Background Monitoring Methods
    
    // Start background monitoring of all GPS-locked points
    startBackgroundGPSMonitoring() {
      console.log('=== START BACKGROUND GPS MONITORING ===')
      
      if (this.backgroundGPSWatcher) {
        console.log('Background GPS monitoring already active')
        return
      }
      
      // Set up watcher for location changes
      this.backgroundGPSWatcher = this.$watch(
        () => [this.currentLocation, this.isValidLocation],
        () => {
          this.checkAllGPSLockedPoints()
        },
        { deep: true, immediate: false }
      )
      
      console.log('Background GPS monitoring started')
    },
    
    // Stop background monitoring
    stopBackgroundGPSMonitoring() {
      console.log('=== STOP BACKGROUND GPS MONITORING ===')
      
      if (this.backgroundGPSWatcher) {
        this.backgroundGPSWatcher()
        this.backgroundGPSWatcher = null
        console.log('Background GPS monitoring stopped')
      }
    },
    
    // Check all GPS-locked points to see if any should be unlocked
    checkAllGPSLockedPoints() {
      if (!this.currentLocation || !this.isValidLocation) {
        return
      }
      
      console.log('=== CHECKING ALL GPS LOCKED POINTS ===')
      
      const lockedPoints = this.getGPSLockedPoints()
      console.log('Checking', lockedPoints.length, 'locked points')
      
      lockedPoints.forEach(point => {
        const pointId = point?.point?.id || point?.data?.point?.id || point?.id
        if (pointId) {
          this.checkPointGPSLock(point, pointId)
        }
      })
    },
    
    // Check if a specific point should be unlocked
    checkPointGPSLock(point, pointId) {
      const gpsLocation = point?.gps_location || point?.data?.gps_location
      const maxDistance = this.getGpsRadius()

      if (!gpsLocation) {
        return
      }
      
      // Parse GPS location
      let pointLat, pointLng
      if (typeof gpsLocation === 'string') {
        const coords = gpsLocation.split(',').map(parseFloat)
        pointLat = coords[0]
        pointLng = coords[1]
      } else if (gpsLocation && typeof gpsLocation === 'object') {
        pointLat = gpsLocation.latitude || gpsLocation.lat
        pointLng = gpsLocation.longitude || gpsLocation.lng
      } else {
        return
      }
      
      // Get current location with proper format handling
      const currentLat = this.currentLocation?.latitude || this.currentLocation?.lat
      const currentLng = this.currentLocation?.longitude || this.currentLocation?.lng
      
      // Validate coordinates
      if (isNaN(pointLat) || isNaN(pointLng) || isNaN(currentLat) || isNaN(currentLng)) {
        return
      }
      
      // Calculate distance
      const distance = this.calculateDistance(currentLat, currentLng, pointLat, pointLng)
      
      // Check if within range
      if (distance <= maxDistance) {
        console.log(`Point ${pointId} is now within range (${Math.round(distance)}m), unlocking`)
        this.unlockGPSPoint(pointId, point)
      }
    },
    
    // Unlock a GPS-locked point
    unlockGPSPoint(pointId, pointData) {
      console.log(`=== UNLOCKING GPS POINT ${pointId} ===`)
      
      // Remove from locked points set
      this.gpsLockedPoints.delete(pointId)
      
      // Add to permanently unlocked points so it stays unlocked
      this.permanentlyUnlockedPoints.add(pointId)
      
      // Save to sessionStorage
      this.savePermanentlyUnlockedPoints()
      
      // Show unlock notification
      // this.$store.dispatch('modal/openModal', {
      //   title: 'Point Unlocked!',
      //   message: `You are now close enough to access this point.`,
      //   type: 'default'
      // })
      
      // // Auto-close notification after 3 seconds
      // setTimeout(() => {
      //   this.$store.dispatch('modal/closeModal')
      // }, 3000)
    },
    
    // Mark a point as GPS locked
    markPointAsGPSLocked(pointId) {
      console.log(`=== MARKING POINT ${pointId} AS GPS LOCKED ===`)
      this.gpsLockedPoints.add(pointId)
    },
    
    // Initialize GPS lock status for all points
    initializeGPSLockStatus() {
      console.log('=== INITIALIZING GPS LOCK STATUS ===')
      
      // Load permanently unlocked points from sessionStorage
      try {
        const storedUnlocked = sessionStorage.getItem('permanentlyUnlockedPoints')
        if (storedUnlocked) {
          this.permanentlyUnlockedPoints = new Set(JSON.parse(storedUnlocked))
          console.log('Loaded permanently unlocked points:', Array.from(this.permanentlyUnlockedPoints))
        }
      } catch (e) {
        console.warn('Failed to load permanently unlocked points from sessionStorage:', e)
      }
      
      Object.values(this.fetchedPoints).forEach(point => {
        const pointId = point?.point?.id || point?.data?.point?.id || point?.id
        if (pointId && this.isOutdoorTour && (point?.settings?.gps_lock || point?.data?.settings?.gps_lock)) {
          // Check if point should be locked based on current distance
          this.checkPointGPSLock(point, pointId)
          
          // If still locked after check, mark as locked
          if (!this.isPointGPSLocked(pointId)) {
            this.markPointAsGPSLocked(pointId)
          }
        }
      })
      
      // Start background monitoring if we have locked points
      if (this.gpsLockedPoints.size > 0) {
        this.startBackgroundGPSMonitoring()
      }
    },
    
    // Refresh point visibility when game state changes
    async refreshPointVisibility() {
      console.log('=== REFRESH POINT VISIBILITY ===')
      
      // Check if component is still mounted
      if (!this._isDestroyed && !this._isBeingDestroyed) {
        console.log('Component is still mounted, proceeding with refresh')
      } else {
        console.log('Component is being destroyed or destroyed, skipping refresh')
        return
      }
      
      if (!this.fetchedPoints || Object.keys(this.fetchedPoints).length === 0) {
        console.log('No points to refresh')
        return
      }
      
      // Only refresh if GameModule is active and ready
      if (!moduleManager.isModuleReady('game', ['processAllPoints'])) {
        console.log('GameModule not ready, skipping refresh')
        return
      }
      
      try {
        console.log('Refreshing point visibility with GameModule')
        
        // Get the GameModule instance
        const gameModule = moduleManager.getModule('game')
        if (gameModule && typeof gameModule.processAllPoints === 'function') {
          const processedPoints = gameModule.processAllPoints(this.fetchedPoints)
          
          console.log('Points reprocessed by GameModule:', processedPoints)
          this.fetchedPoints = processedPoints
        } else {
          console.log('GameModule not available or processAllPoints method not found')
        }
        
        // Force reactivity update by triggering a re-evaluation
        // Only if component is still mounted
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.$nextTick(() => {
            // Double-check component is still mounted before forcing update
            if (!this._isDestroyed && !this._isBeingDestroyed) {
              console.log('Forcing reactivity update')
              // This will trigger the visiblePoints computed property to re-evaluate
              this.$forceUpdate()
            } else {
              console.log('Component destroyed during nextTick, skipping forceUpdate')
            }
          })
        } else {
          console.log('Component destroyed, skipping nextTick and forceUpdate')
        }
        
        console.log('Point visibility refreshed')
      } catch (error) {
        console.error('Error refreshing point visibility:', error)
      }
    },
    
    // Manual method to test visibility updates (for debugging)
    testVisibilityUpdate() {
      console.log('=== MANUAL VISIBILITY UPDATE TEST ===')
      console.log('Current fetchedPoints:', this.fetchedPoints)
      console.log('Current visiblePoints:', this.visiblePoints)
      console.log('GameModule active:', moduleManager.isModuleActive('game'))
      console.log('GameModule ready:', moduleManager.isModuleReady('game', ['shouldShowPoint']))
      
      if (moduleManager.isModuleReady('game', ['shouldShowPoint'])) {
        const gameModule = moduleManager.getModule('game')
        console.log('GameModule instance:', gameModule)
        console.log('GameModule state:', gameModule?.getGameState())
        
        // Test each point's visibility
        Object.entries(this.fetchedPoints).forEach(([pointId, pointData]) => {
          const shouldShow = gameModule?.shouldShowPoint(pointData)
          console.log(`Point ${pointId} shouldShow: ${shouldShow}`)
        })
      }
      
      // Trigger refresh
      this.refreshPointVisibility()
    },
    
    // Manual method to test tourSettings watcher (for debugging)
    testTourSettingsWatcher() {
      console.log('=== MANUAL TOUR SETTINGS WATCHER TEST ===')
      console.log('Current tourSettings:', this.tourSettings)
      console.log('Current fetchedPoints count:', Object.keys(this.fetchedPoints).length)
      
      // Manually trigger the watcher by simulating a settings change
      const currentSettings = this.tourSettings
      if (currentSettings) {
        // Create a shallow copy to trigger the watcher
        const newSettings = { ...currentSettings }
        this.$store.commit('settings/SET_TOUR_SETTINGS', newSettings)
      }
    },
    
    // Debug overlay state and fix issues
    debugOverlayState() {
      console.log('=== DEBUG OVERLAY STATE ===')
      
      // Debug current overlay state
      this.$store.dispatch('overlay/debugOverlayState')
      
      // Check if overlay is in a bad state and reset if needed
      const isVisible = this.$store.getters['overlay/isVisible']
      const overlayComponent = this.$store.getters['overlay/overlayComponent']
      const overlayTitle = this.$store.getters['overlay/overlayTitle']
      const overlayProps = this.$store.getters['overlay/overlayProps']
      
      console.log('Overlay getters:', {
        isVisible,
        overlayComponent,
        overlayTitle,
        overlayProps
      })
      
      if (isVisible && !overlayComponent) {
        console.log('Overlay is visible but has no component - resetting state')
        this.$store.dispatch('overlay/resetOverlayState')
      }
    },
    
    // Comprehensive debug for point opening
    debugPointOpening(pointId) {
      console.log('=== DEBUG POINT OPENING ===')
      console.log('Point ID:', pointId)
      console.log('Current route:', this.$route.path)
      console.log('Current route params:', this.$route.params)
      console.log('Fetched points count:', Object.keys(this.fetchedPoints).length)
      console.log('Point in fetchedPoints:', !!this.fetchedPoints[pointId])
      
      // Check overlay state before
      console.log('Overlay state BEFORE opening point:')
      this.debugOverlayState()
      
      // Try to open the point
      console.log('Attempting to open point...')
      this.showPointFromId(pointId)
      
      // Check overlay state after a delay
      setTimeout(() => {
        console.log('Overlay state AFTER opening point:')
        this.debugOverlayState()
      }, 1000)
    },
    
    // Force overlay reset (for debugging)
    forceOverlayReset() {
      console.log('=== FORCE OVERLAY RESET ===')
      this.$store.dispatch('overlay/resetOverlayState')
      console.log('Overlay state forced to reset')
    },
    
    // New method to reprocess points with GameModule
    reprocessPointsWithGameModule() {
      console.log('=== REPROCESS POINTS WITH GAME MODULE ===')
      
      // Check if component is being destroyed
      if (this._isBeingDestroyed) {
        console.log('Component is being destroyed, skipping reprocessing')
        return
      }
      
      if (!this.fetchedPoints || Object.keys(this.fetchedPoints).length === 0) {
        console.log('No points to reprocess')
        return
      }
      
      if (!moduleManager.isModuleReady('game', ['processAllPoints'])) {
        console.log('GameModule not ready, skipping reprocessing')
        return
      }
      
      try {
        console.log('Reprocessing points with GameModule')
        
        const gameModule = moduleManager.getModule('game')
        if (gameModule && typeof gameModule.processAllPoints === 'function') {
          const processedPoints = gameModule.processAllPoints(this.fetchedPoints)
          
          console.log('Points reprocessed by GameModule:', processedPoints)
          this.fetchedPoints = processedPoints
        } else {
          console.log('GameModule not available or processAllPoints method not found')
        }
      } catch (error) {
        console.error('Error reprocessing points:', error)
      }
    },

    // Method to process pending point openings after points are loaded
    processPendingPointOpenings() {
      console.log('=== PROCESSING PENDING POINT OPENINGS ===')
      console.log('Pending openings count:', this.pendingPointOpenings.length)
      console.log('Pending openings:', this.pendingPointOpenings)
      console.log('Fetched points count:', Object.keys(this.fetchedPoints).length)
      
      if (this.pendingPointOpenings.length === 0) {
        console.log('No pending point openings to process.')
        return
      }

      const pointIdsToProcess = [...this.pendingPointOpenings];
      this.pendingPointOpenings = []; // Clear the queue

      console.log('Processing point IDs:', pointIdsToProcess)
      
      pointIdsToProcess.forEach(pointId => {
        console.log('Processing pending point opening:', pointId);
        this.showPointFromId(pointId);
      });
      console.log('All pending point openings processed.');
    },
    
    // Save permanently unlocked points to sessionStorage
    savePermanentlyUnlockedPoints() {
      try {
        sessionStorage.setItem('permanentlyUnlockedPoints', JSON.stringify(Array.from(this.permanentlyUnlockedPoints)))
      } catch (e) {
        console.warn('Failed to save permanently unlocked points to sessionStorage:', e)
      }
    }
  },
  beforeUnmount() {
    console.log('=== POINTS COMPONENT BEFORE UNMOUNT ===')
    
    // Mark component as being destroyed to prevent new operations
    this._isBeingDestroyed = true
    
    // Remove event listeners
    try {
      moduleManager.removeEventListener('game:state_updated', this.refreshPointVisibility.bind(this))
      moduleManager.removeEventListener('game:inventory_updated', this.refreshPointVisibility.bind(this))
    } catch (error) {
      console.error('Error removing module event listeners:', error)
    }
    
    // Stop location watching when component is unmounted
    try {
      this.stopLocationWatching()
    } catch (error) {
      console.error('Error stopping location watching:', error)
    }
    
    // Clean up GPS lock watcher if active
    if (this.gpsLockWatcher) {
      try {
        this.gpsLockWatcher()
        this.gpsLockWatcher = null
      } catch (error) {
        console.error('Error cleaning up GPS lock watcher:', error)
      }
    }
    
    // Stop background GPS monitoring
    try {
      this.stopBackgroundGPSMonitoring()
    } catch (error) {
      console.error('Error stopping background GPS monitoring:', error)
    }
    
    // Clean up background GPS watcher if active
    if (this.backgroundGPSWatcher) {
      try {
        this.backgroundGPSWatcher()
        this.backgroundGPSWatcher = null
      } catch (error) {
        console.error('Error cleaning up background GPS watcher:', error)
      }
    }
    
    console.log('Points component cleanup completed')
  }
}
</script>